import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, GridColumn, GridToolbar, } from "@progress/kendo-react-grid";
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { PanelBar, PanelBarItem, StackLayout } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import {
  createDocument,
  deleteDocument,
  updateDocument,
} from '../../actions/index.js'
import Dialog from '../../components/Dialog/Dialog';
import EditDocumentForm from '../../components/EditDocumentForm.jsx';
import { process } from '@progress/kendo-data-query';
import { CheckmarkCell, DateCell, EditCommandCell, GroupCell } from '../../components/GridCell'
import { formCloser } from '../../utils/formCloser.js';
import { esriSuggest } from '../../actions/portal/esri/esriSuggest.js';
import { clearMessage, setMessage } from '../../store/actions/dialogActions.js';

const initialDataState = {
  skip: 0,
  take: 10,
  // sort: [
  //   { field: 'lastName', dir: 'asc' }
  // ],
}

const DashboardDocuments = () => {
  const _export = React.useRef(null);

  const { token, user } = useSelector(state => state.authReducer);
  const { documents, totalFilesize, folders } = useSelector(state => state.documentReducer)
  const { esriSuggestions } = useSelector(state => state.esriReducer);
  const { groups } = useSelector(state => state.groupsReducer);
  const { userFilter } = useSelector(state => state.filterReducer);

  const [openForm, setOpenForm] = useState(false);
  const [dataState, setDataState] = useState(initialDataState);
  const [collapsed, setCollapsed] = useState([]);
  const [dialogProps, setDialogProps] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [selectedDocument, setSelectedDocument] = useState({});
  const [folderizedDocuments, setFolderizedDocuments] = useState([]);
  const [showDialog, setShowDialog] = useState(false);

  const colorMap = {};

  let colorIndex = 0;

  let colorOptions = [
    "#2962FF",
    "#2979FF",
    "#448AFF",
    "#82B1FF",
    "#0D47A1",
    "#1565C0",
    "#1976D2",
    "#1E88E5",
    "#2196F3",
    "#42A5F5",
    "#64B5F6",
    "#90CAF9",
    "#BBDEFB",
    "#E3F2FD",
  ];

  const parsedDocuments = documents.map(u => {
    let copy = { ...u };
    copy.stringGroups = u.Groups.map(g => g.name).join(', ');
    copy.username = u.submittedUser ? u.submittedUser.firstName + ' ' + u.submittedUser.lastName : '';
    return copy;
  }).filter((document) => {
    return !collapsed.includes(document.folderId);
  });

  const collapseFolder = ({ id, folderId }) => {
    const findId = (id) => {
      const i = collapsed.indexOf(id);
      if (i > -1) {
        let c = collapsed.slice()
        c.splice(i, 1)
        setCollapsed(c);
      } else {
        setCollapsed(collapsed.concat([id]));
      }
    }
    findId(folderId ?? id);
  }

  useEffect(() => {
    let noFolder = { file: null, filename: 'No Folder', filesize: 0, children: [] };
    let data = [];
    documents.forEach(document => {
      if (document.isFolder) {
        data.push(document);
      } else {
        noFolder.children.push(document);
      }
    })
    data.unshift(noFolder);
    setFolderizedDocuments(data);
  }, [documents]);

  const dispatch = useDispatch();

  const enterEdit = (item) => {
    setOpenForm(true);
    setSelectedDocument(item);
  }

  const handleCancelEdit = () => {
    setOpenForm(false);
  };

  const deleteEntry = (dataItem) => {
    // prompt for confirmation
    setDialogProps({
      title: `Delete Document`,
      text: `Are you sure you want to delete this record?`,
      onConfirm: () => {
        dispatch(deleteDocument(dataItem.id, token, formCloser(setShowDialog)))
      },
      enableCancelBtn: true
    });

    setShowDialog(true);
  }


  const editFormSubmit = (event, files) => {
    dispatch(setMessage({ type: 'LOAD' }))
    //Get File Data
    if (files && files[0]) {
      let reader = new FileReader();
      reader.readAsDataURL(files[0].getRawFile());
      reader.onload = () => {
        let fileData = {
          "filename": files[0].name,
          "groups": event.Groups,
          "filesize": files[0].size,
          "file": reader.result,
          "folderId": event.folderId,
        };
        
        if (!event.id) {
          dispatch(createDocument(fileData, token, formCloser(setOpenForm), user));
        } else {
          dispatch(updateDocument(event.id, fileData, token, formCloser(setOpenForm), user))
        }
      }
      reader.onerror = () => {
        dispatch(setMessage({
          title: 'File Upload Failed',
          message: "An error occurred during file upload",
          type: 'ERR',
        }));
      }
    } else if (event.hyperlink) {
      let fileData = {
        "filename": event.description ?? event.filename,
        "groups": event.Groups,
        "filesize": 0,
        "file": event.hyperlink ?? event.file,
        "folderId": event.folderId,
      };

      if (!event.id) {
        dispatch(createDocument(fileData, token, formCloser(setOpenForm), user));
      } else {
        dispatch(updateDocument(event.id, fileData, token, formCloser(setOpenForm), user))
      }
    } else if (event.folderName) {
      let fileData = {
        "filename": event.folderName,
        "groups": event.Groups,
        "filesize": 0,
        "file": null,
        "folderId": event.folderId,
      };

      if (!event.id) {
        dispatch(createDocument(fileData, token, formCloser(setOpenForm), user));
      } else {
        dispatch(updateDocument(event.id, fileData, token, formCloser(setOpenForm), user))
      }
    } else if (event.id) {
      let fileData = {
        "filename": event.filename ?? '',
        "groups": event.Groups,
        "filesize": event.filesize ?? 0,
        "file": event.file ?? null,
        "folderId": event.folderId,
      };
      dispatch(updateDocument(event.id, fileData, token, formCloser(setOpenForm), user))
    }
  }

  return (
    <div>
      <ExcelExport ref={_export} data={parsedDocuments}>
        <Grid
          {...dataState}
          data={process(parsedDocuments, dataState)}
          onDataStateChange={e => setDataState(e.dataState)}
          pageable={true}
          sortable={true}
          className='flo-document-grid'
        >
          <GridToolbar>
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}
              >
                {(!user.viewOnly && !user.notificationOnly) ? <Button
                  className="k-button k-primary"
                  style={{
                    minHeight: 25,
                    padding: '10px 20px',
                    marginLeft: 15,
                  }}
                  onClick={() => {
                    enterEdit({
                      filename: "",
                      Groups: [],
                      type: 'file',
                      file: null
                    })
                  }}
                  iconClass="k-icon k-i-plus"
                >
                  Add Document
                </Button> : null}
                {(!user.viewOnly && !user.notificationOnly) ? <Button
                  className="k-button k-primary"
                  style={{
                    minHeight: 25,
                    padding: '10px 20px',
                    marginLeft: 15,
                  }}
                  onClick={() => {
                    enterEdit({
                      filename: "",
                      Groups: [],
                      type: 'folder',
                      file: null
                    })
                  }}
                  iconClass="k-icon k-i-plus"
                >
                  Add Folder
                </Button> : null}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  paddingRight: '20px'
                }}
              >
                {`${((totalFilesize/1000000)).toFixed(2)} / 500 MB`}
              </div>
            </div>
          </GridToolbar>
          <GridColumn
            field="folderId"
            width={10}
            cell={props => {
              const expression = /[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gi;
              let isFolder = true;
              let hasFolder = false;
              let color = 'white';

              if (props?.dataItem?.folderId) {
                hasFolder = true;
                isFolder = false;
              } else {
                if (props?.dataItem?.file) {
                  if (props?.dataItem?.file.match(expression) || props?.dataItem?.filesize > 0) { //is a link, cant be folder
                    isFolder = false;
                  }
                }
              }

              if (isFolder) {
                color = '#034580';
              } else {
                if (!hasFolder) {
                  color = 'rgb(100, 181, 246)';
                }
              }

              return (<td onClick={(e) => {collapseFolder({ id: props.dataItem.id, folderId: props.dataItem.folderId })}} style={{ backgroundColor: color, cursor: 'pointer' }}/>)
            }}
          />
          <GridColumn field="filename" title="File Name" />
          <GridColumn   
            field="filesize" 
            title="File Size" 
            cell={props => 
              <td><div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  overflow: 'auto',
                  whiteSpace: 'nowrap'
                }}>
                {props.dataItem.filesize ? ((props.dataItem.filesize / 1000000).toFixed(2)) + ' MB' : ''}
              </div>
              </td>}
          />
          <GridColumn   
            field="folderId" 
            title="Folder" 
            cell={props => 
              <td><div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  overflow: 'auto',
                  whiteSpace: 'nowrap'
                }}>
                {props.dataItem.folderId ? folders[props.dataItem.folderId] ? folders[props.dataItem.folderId].filename : '' : ''}
              </div>
              </td>}
          />
          <GridColumn
            field="stringGroups"
            title="Groups"
            cell={props => <GroupCell groups={props.dataItem.Groups} />}
          />
          <GridColumn
            title="Created"
            field="createdAt"
            cell={props => <DateCell date={props.dataItem.createdAt} />}
            width={150}
          />
          <GridColumn
            title="Download"
            field="download"
            cell={props => 
              props.dataItem.file ? <td style={{ overflow: 'visible' }}>
                <Button 
                className="k-button k-primary"
                style={{ width: '108px' }}
                onClick={() => {
                  var link = document.createElement("a");
                  link.download = props.dataItem.filename;
                  link.href = props.dataItem.file;
                  link.target = '_target';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                  {props.dataItem.file?.includes("http") && props.dataItem.filesize == 0 ? "Open" : "Download"}
                </Button>
              </td> : <td/>}
          />
          <GridColumn 
            title="Created By"
            field="username"
          />
          <GridColumn
            cell={(props) => (
              <EditCommandCell
                {...props}
                user={user}
                enterEdit={enterEdit}
                deleteEntry={(dataItem) => deleteEntry(dataItem)}
              />
            )}
            width={100}
          />
        </Grid>
      </ExcelExport>
      {openForm && (
        <EditDocumentForm
          groups={groups}
          folders={folders}
          cancelEdit={handleCancelEdit}
          onSubmit={editFormSubmit}
          item={selectedDocument}
        />
        )}
      {showDialog &&
        <Dialog
          {...dialogProps}
          onClose={() => setShowDialog(false)}
        />}
    </div>
  )
}

export default DashboardDocuments;